import { ProblemReport } from './components/problem-report';
import { StatePanel } from '@hoedokloop/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { createWebPlatformAdapters } from './platform/web-adapters';

type State = { hasError: boolean; referenceId?: string };

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const result = createWebPlatformAdapters().errors.capture(error, {
      componentStack: info.componentStack ? 'present' : 'absent',
    });
    if (result.ok) this.setState({ referenceId: result.value.referenceId });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <main className="pn-fatal-error">
          <StatePanel
            action={{ label: '앱 다시 열기', onSelect: () => window.location.assign('/') }}
            kind="error-terminal"
            preservedDataMessage="기기에 저장을 마친 학습 기록은 그대로예요."
            {...(this.state.referenceId === undefined
              ? {}
              : { referenceId: this.state.referenceId })}
          />
          <ProblemReport />
        </main>
      );
    }
    return this.props.children;
  }
}

export function RouteErrorPage() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <main className="pn-fatal-error">
      <StatePanel
        action={{ label: '홈으로 가기', onSelect: () => window.location.assign('/') }}
        description={
          notFound
            ? '주소가 바뀌었거나 없는 화면이에요. 홈에서 다시 시작할 수 있어요.'
            : '화면을 여는 중 문제가 생겼어요. 기기에 저장을 마친 기록은 그대로예요.'
        }
        kind={notFound ? 'empty-filtered' : 'error-recoverable'}
        {...(notFound ? { title: '이 화면을 찾을 수 없어요' } : {})}
      />
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main className="pn-fatal-error">
      <StatePanel
        action={{ label: '홈으로 가기', onSelect: () => window.location.assign('/') }}
        description="주소가 바뀌었거나 없는 화면이에요. 홈에서 다시 시작할 수 있어요."
        kind="empty-filtered"
        title="이 화면을 찾을 수 없어요"
      />
    </main>
  );
}
